import React, { useState, useEffect } from "react";
import { Box, TextField, IconButton, CircularProgress, Tooltip } from "@mui/material";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import { useVoiceInput } from "../../hooks/useVoiceInput";

export const InputArea = ({ input, setInput, isLoading, onSubmit }) => {
  const [voiceError, setVoiceError] = useState(null);

  // Handle transcript updates from voice input
  const handleTranscript = (transcript, isFinal) => {
    if (isFinal) {
      setInput(transcript);
      setVoiceError(null);
    } else {
      // Show interim results in real-time
      setInput(transcript);
    }
  };

  const {
    isListening,
    isSupported,
    error: voiceInputError,
    toggleListening,
  } = useVoiceInput(handleTranscript);

  // Clear voice error after a delay
  useEffect(() => {
    if (voiceInputError) {
      setVoiceError(voiceInputError);
      const timer = setTimeout(() => setVoiceError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [voiceInputError]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;
    onSubmit(e);
  };

  const handleMicClick = () => {
    if (!isSupported) {
      setVoiceError("Voice input is not supported in your browser.");
      return;
    }
    
    // If starting to listen, clear the input field for fresh voice input
    if (!isListening) {
      setInput("");
      setVoiceError(null);
    }
    
    toggleListening();
  };

  return (
    <Box>
      {voiceError && (
        <Box
          sx={{
            mb: 1,
            px: 1.5,
            py: 0.75,
            backgroundColor: "#fef2f2",
            border: "1px solid #fecaca",
            borderRadius: 1,
            fontSize: "0.75rem",
            color: "#dc2626",
          }}
        >
          {voiceError}
        </Box>
      )}
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          mt: 1.5,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          border: "1.5px solid #e2e8f0",
          borderRadius: 2,
          backgroundColor: "#ffffff",
          px: 1.5,
          py: 0.75,
          transition: "all 0.2s ease",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
          "&:focus-within": {
            border: "1.5px solid #3F49A7",
            boxShadow: "0 4px 12px rgba(63, 73, 167, 0.15)",
            transform: "translateY(-1px)",
          },
          "&:hover:not(:focus-within)": {
            borderColor: "#cbd5e1",
            boxShadow: "0 2px 6px rgba(0, 0, 0, 0.08)",
          },
        }}
      >
        <TextField
          fullWidth
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask about your data warehouse, SQL, metrics…"
          disabled={isLoading}
          variant="standard"
          InputProps={{
            disableUnderline: true,
            sx: {
              color: "#1e293b",
              fontSize: "0.8125rem",
              "&::placeholder": {
                color: "#94a3b8",
                opacity: 1,
                fontSize: "0.8125rem",
              },
            },
          }}
          sx={{
            "& .MuiInputBase-input": {
              py: 0.5,
            },
          }}
        />
        <Tooltip title={isListening ? "Stop recording" : "Start voice input"}>
          <IconButton
            onClick={handleMicClick}
            disabled={isLoading || !isSupported}
            size="small"
            sx={{
              color: isListening ? "#dc2626" : "#64748b",
              backgroundColor: isListening ? "#fee2e2" : "transparent",
              padding: 0.75,
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: isListening ? "#fecaca" : "#f1f5f9",
                transform: "scale(1.05)",
              },
              "&:active": {
                transform: "scale(0.95)",
              },
              "&:disabled": {
                color: "#cbd5e1",
                backgroundColor: "transparent",
              },
              animation: isListening
                ? "pulse 1.5s ease-in-out infinite"
                : "none",
              "@keyframes pulse": {
                "0%, 100%": {
                  opacity: 1,
                },
                "50%": {
                  opacity: 0.7,
                },
              },
            }}
          >
            {isListening ? (
              <MicOffIcon sx={{ fontSize: 18 }} />
            ) : (
              <MicIcon sx={{ fontSize: 18 }} />
            )}
          </IconButton>
        </Tooltip>
        <IconButton
          type="submit"
          disabled={isLoading || !input.trim()}
          size="small"
          sx={{
            color: "#ffffff",
            backgroundColor: "#3F49A7",
            padding: 0.75,
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: "#2c357a",
              transform: "scale(1.05)",
              boxShadow: "0 2px 8px rgba(63, 73, 167, 0.3)",
            },
            "&:active": {
              transform: "scale(0.95)",
            },
            "&:disabled": {
              backgroundColor: "#cbd5e1",
              color: "#94a3b8",
              opacity: 0.6,
            },
          }}
        >
          {isLoading ? (
            <CircularProgress size={18} sx={{ color: "#ffffff" }} />
          ) : (
            <SendRoundedIcon sx={{ fontSize: 18 }} />
          )}
        </IconButton>
      </Box>
    </Box>
  );
};

